import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import api from '../api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Tokyo');

function formatChatTime(utcTime) {
  if (!utcTime) return '';
  const jst = dayjs.utc(utcTime).tz('Asia/Tokyo');
  const diffMin = dayjs().diff(jst, 'minute');

  if (diffMin < 60) return `${diffMin}分前`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}時間前`;
  return jst.format('MM/DD HH:mm');
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return 'N/A';
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  const kb = b / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

function ChatDownloadDialog({ open, loading, target, onClose, onDownload }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>ファイルをダウンロード</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">準備中...</Typography>
          </Box>
        )}

        {!loading && target?.error && (
          <Typography variant="body2" color="error">
            ダウンロード情報の取得に失敗しました。
          </Typography>
        )}

        {!loading && target && !target.error && (
          <>
            <Typography variant="body2">ファイル名：{target.fileName}</Typography>
            <Typography variant="body2">ファイルサイズ：{formatFileSize(target.fileSize)}</Typography>
            <Typography variant="body2">有効期限：{target.expiresIn}</Typography>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              ※ ダウンロードURLは一時的なものです。<br />
              ※ 再度ダウンロードする場合は、画面を再読み込みしてください。
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          disabled={loading || !target || target?.error}
          onClick={onDownload}
        >
          ダウンロード
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ExternalSettings({ roomId }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState('');
  const urlInputRef = useRef(null);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [expiresAt, setExpiresAt] = useState(''); // YYYY-MM-DD

  const load = async () => {
    if (!roomId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/chats/${roomId}/external`);
      const d = res.data || {};
      setInfo(d);
      setEnabled(!!d.enabled);
      setExpiresAt(d.expiresAt ? dayjs(d.expiresAt).format('YYYY-MM-DD') : '');
      setPassword('');
    } catch (e) {
      console.error('[external settings load]', e);
      setInfo({ error: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const createOrSet = async () => {
    if (!roomId) return;
    if (!password) return;

    setSaving(true);
    try {
      const res = await api.post(`/api/chats/${roomId}/external`, {
        password,
        expiresAt: expiresAt ? `${expiresAt} 23:59:59` : null,
      });
      setInfo({ ...(info || {}), ...res.data, hasPassword: true, enabled: true });
      setEnabled(true);
      setPassword('');
      await load();
    } catch (e) {
      console.error('[external settings create]', e);
    } finally {
      setSaving(false);
    }
  };

  const update = async () => {
    if (!roomId) return;

    setSaving(true);
    try {
      const body = {
        enabled,
        expiresAt: expiresAt ? `${expiresAt} 23:59:59` : null,
      };
      if (password) body.password = password;

      const res = await api.put(`/api/chats/${roomId}/external`, body);
      setInfo(res.data);
      setPassword('');
    } catch (e) {
      console.error('[external settings update]', e);
    } finally {
      setSaving(false);
    }
  };

  // URL 未発行なら確認なしで発行、発行済みなら確認ダイアログを挟む (xlsx 行94)
  const requestRegenerate = () => {
    if (!roomId) return;
    if (info?.url) {
      setConfirmRegenerate(true);
      return;
    }
    regenerate();
  };

  const regenerate = async () => {
    if (!roomId) return;
    setConfirmRegenerate(false);
    setSaving(true);
    try {
      await api.post(`/api/chats/${roomId}/external/regenerate`);
      await load();
    } catch (e) {
      console.error('[external settings regenerate]', e);
    } finally {
      setSaving(false);
    }
  };

  // 現在有効な URL をコピー (xlsx 行94)
  const copyUrl = async () => {
    const url = info?.url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied('コピーしました');
    } catch {
      // クリップボードが使えない環境では URL 欄を選択状態にして手動コピーを促す
      urlInputRef.current?.select();
      setCopied('URL欄を選択しました（Cmd+C / Ctrl+C でコピー）');
    }
    setTimeout(() => setCopied(''), 3000);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, background: '#fcfcfc' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
        外部チャット設定
      </Typography>
      <Divider sx={{ my: 1 }} />

      {loading && (
        <Typography variant="body2" color="text.secondary">
          読み込み中...
        </Typography>
      )}

      {!loading && info?.error && (
        <Typography variant="body2" color="error">
          外部チャット設定の取得に失敗しました。
        </Typography>
      )}

      {!loading && info && !info.error && (
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2">
              状態：{enabled ? '有効' : '無効'}
            </Typography>
            <Typography variant="body2">
              パスワード：{info.hasPassword ? '設定済み' : '未設定'}
            </Typography>
            <Typography variant="body2">
              有効期限：{expiresAt || 'なし'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
            <TextField
              size="small"
              label="有効期限（任意）"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={saving}
            />
            <TextField
              size="small"
              label={info.hasPassword ? '新しいパスワード（任意）' : 'パスワード（必須）'}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={saving}
              sx={{ minWidth: 260 }}
            />
            <Button
              variant="contained"
              onClick={info.hasPassword ? update : createOrSet}
              disabled={saving || (!info.hasPassword && !password)}
            >
              {saving ? '保存中' : '保存'}
            </Button>
            <Button variant="outlined" color="warning" onClick={requestRegenerate} disabled={saving}>
              {info.url ? 'URL再発行（現URL無効化）' : 'URL発行'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="現在有効な外部チャットURL"
              value={info.url || '未発行（右の「URL発行」から発行してください）'}
              InputProps={{ readOnly: true }}
              helperText={
                info.url
                  ? 'このURLをクライアントへ共有してください（パスワード変更してもURLは変わりません）'
                  : ''
              }
              onFocus={(e) => info.url && e.target.select()}
              inputRef={urlInputRef}
              sx={{ flex: 1, minWidth: 360 }}
            />
            <Button variant="outlined" onClick={copyUrl} disabled={!info.url}>
              {copied || 'コピー'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.open(info.url, '_blank', 'noopener')}
              disabled={!info.url}
            >
              開く
            </Button>
            <Button
              variant="outlined"
              color={enabled ? 'error' : 'primary'}
              onClick={async () => {
                setEnabled((v) => !v);
                setTimeout(update, 0);
              }}
              disabled={saving}
            >
              {enabled ? '無効化' : '有効化'}
            </Button>
          </Box>

          <Dialog open={confirmRegenerate} onClose={() => setConfirmRegenerate(false)}>
            <DialogTitle>外部チャットURLを再発行しますか？</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                再発行すると現在のURLは無効になり、クライアントへ共有済みのリンクは使えなくなります。
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmRegenerate(false)}>キャンセル</Button>
              <Button variant="contained" color="warning" onClick={regenerate} disabled={saving}>
                再発行する
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Paper>
  );
}

export default function ProjectChat({ projectId, user }) {
  const [messages, setMessages] = useState([]);
  const [roomId, setRoomId] = useState(null);

  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);

  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadTarget, setDownloadTarget] = useState(null);

  const messagesEndRef = useRef(null);

  const canSend = useMemo(() => {
    return !sending && (!!message.trim() || !!file) && !!roomId;
  }, [sending, message, file, roomId]);

  useEffect(() => {
    if (!projectId) return;
    loadOrCreateRoom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadOrCreateRoom = async () => {
    try {
      const roomsRes = await api.get('/api/chats');
      const existing = (roomsRes.data || []).find(
        (r) => Number(r.projectId) === Number(projectId)
      );

      if (existing) {
        setRoomId(existing.id);
        await loadMessages(existing.id);
      } else {
        const newRoom = await api.post('/api/chats', {
          projectId,
          type: 'customer',
          createdBy: user.id,
        });
        setRoomId(newRoom.data.id);
        setMessages([]);
      }
    } catch (err) {
      console.error('[ChatRoom load]', err);
    }
  };

  const loadMessages = async (id) => {
    setLoadingMessages(true);
    try {
      const res = await api.get(`/api/chats/${id}/messages`);
      setMessages(res.data || []);
    } catch (err) {
      console.error('[loadMessages]', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendMessage = async () => {
    if (!canSend) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('senderId', user.id);
      formData.append('message', message || '');
      formData.append('isFromExternal', 'false');
      if (file) formData.append('file', file);

      await api.post(`/api/chats/${roomId}/messages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setMessage('');
      setFile(null);
      await loadMessages(roomId);
    } catch (err) {
      console.error('[sendMessage]', err);
    } finally {
      setSending(false);
    }
  };

  const openDownloadModal = async (fileId) => {
    if (!roomId || !fileId) return;

    setDownloadOpen(true);
    setDownloadLoading(true);
    setDownloadTarget(null);

    try {
      const res = await api.get(`/api/chats/${roomId}/files/${fileId}/download`);
      setDownloadTarget(res.data);
    } catch (err) {
      console.error('[download]', err);
      setDownloadTarget({ error: true });
    } finally {
      setDownloadLoading(false);
    }
  };

  const closeDownloadModal = () => {
    setDownloadOpen(false);
    setDownloadTarget(null);
    setDownloadLoading(false);
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ p: 2, mt: 2, height: 500, display: 'flex', flexDirection: 'column' }}
      >
        <Typography variant="h6" gutterBottom>
          チャット
        </Typography>
        <Divider sx={{ mb: 1 }} />

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            mb: 2,
            p: 1,
            border: '1px solid #ddd',
            borderRadius: 1,
            background: '#fafafa',
          }}
        >
          {loadingMessages && messages.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              読み込み中...
            </Typography>
          )}

          {!loadingMessages && messages.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              まだメッセージはありません。
            </Typography>
          )}

          {messages.map((m) => (
            <Box
              key={m.id}
              sx={{
                mb: 1.5,
                display: 'flex',
                flexDirection: m.senderId === String(user.id) ? 'row-reverse' : 'row',
              }}
            >
              <Paper
                sx={{
                  px: 1.5,
                  py: 1,
                  maxWidth: '70%',
                  background: m.senderId === String(user.id) ? '#e0f7fa' : '#fff',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {m.senderName || (m.isFromExternal ? '外部ユーザー' : '社内')}
                </Typography>

                {m.message && <Typography variant="body2">{m.message}</Typography>}

                {m.fileId && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      color: 'primary.main',
                      userSelect: 'none',
                    }}
                    onClick={() => openDownloadModal(m.fileId)}
                  >
                    📎 {m.fileName || '添付ファイル'}
                  </Typography>
                )}

                <Typography variant="caption" color="text.secondary">
                  {formatChatTime(m.createdAt)}
                </Typography>
              </Paper>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>

        {file && (
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#f5f5f5',
            }}
          >
            <Typography variant="body2">
              📎 {file.name}（{formatFileSize(file.size)}）
            </Typography>
            <Button size="small" color="error" onClick={() => setFile(null)} disabled={sending}>
              削除
            </Button>
          </Paper>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button component="label" variant="outlined" size="small" disabled={sending}>
            添付
            <input type="file" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Button>

          <TextField
            fullWidth
            placeholder="メッセージを入力..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={sending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />

          <Button variant="contained" onClick={sendMessage} disabled={!canSend}>
            {sending ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                送信中
              </>
            ) : (
              '送信'
            )}
          </Button>
        </Box>

        <ChatDownloadDialog
          open={downloadOpen}
          loading={downloadLoading}
          target={downloadTarget}
          onClose={closeDownloadModal}
          onDownload={() => {
            if (!downloadTarget?.signedUrl) return;
            window.open(downloadTarget.signedUrl, '_blank', 'noopener,noreferrer');
            closeDownloadModal();
          }}
        />
      </Paper>

      {/* 外部チャット設定（内部チャットとは独立） */}
      {roomId && <ExternalSettings roomId={roomId} />}
    </>
  );
}
